import Utils from "Common/Utils";
import React, { useCallback, useEffect, useRef, useState } from "react";

type HierachicalDropdownProps = {
  id: string;
  columnOptions: any[];
  onClick: ((item: any) => void)[];
  selected: any[];
  data: any[][];
  isLoading?: { [key: number]: boolean };
}

const HierachicalDropdown = ({ id, columnOptions, onClick, selected, data,isLoading = {}}: HierachicalDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  
  const isAnyLoading = Object.values(isLoading).some(loading => loading === true);

  const handleClickOutside = useCallback(
    (e: MouseEvent) =>
      Utils.run_callback_on_click_outside_ref(e, ref, () => setIsOpen(false)),
    [ref]
  );

  useEffect(() => {
    isOpen
      ? document.addEventListener("mousedown", handleClickOutside)
      : document.removeEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, handleClickOutside, isOpen]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, index: number, item: any) => {
    e.preventDefault();
    
    if (isAnyLoading) {
      console.log('Operation in progress, please wait...');
      return;
    }
    
    if (isLoading[index]) {
      console.log(`Column ${index} is loading, please wait...`);
      return;
    }

    onClick[index](item);
    if (index + 1 === data.length) setIsOpen(false);
  };

  const calculateMaxHeight = () => {
    const headerHeight = 32;
    const itemHeight = 30;
    const emptyStateHeight = 60;
    const maxVisibleItems = 6;
    
    let maxHeight = headerHeight + emptyStateHeight;
    
    columnOptions?.forEach((_: any, index: number) => {
      const itemCount = data[index] && Array.isArray(data[index]) ? data[index].length : 0;
      
      if (itemCount === 0) {
        const height = headerHeight + emptyStateHeight;
        maxHeight = Math.max(maxHeight, height);
      } else {
        const height = Math.min(
          headerHeight + (itemCount * itemHeight) + 8,
          headerHeight + (maxVisibleItems * itemHeight) + 8
        );
        maxHeight = Math.max(maxHeight, height);
      }
    });
    
    return maxHeight;
  };

  const maxDropdownHeight = calculateMaxHeight();

  return (
    <div id={id} className={`tw-relative`} ref={ref}>
      <button
        id={`${id}-toggle-button`}
        onClick={() => !isAnyLoading && setIsOpen(!isOpen)}
        disabled={isAnyLoading}
        className={`tw-h-full tw-flex tw-items-center ${
          isOpen ? 'tw-bg-[#EBECF0]' : ''
        } hover:tw-bg-[#EBECF0] tw-border tw-py-1.5 tw-rounded tw-px-2 tw-w-auto ${
          isAnyLoading ? 'tw-opacity-60 tw-cursor-wait' : 'tw-cursor-pointer'
        }`}
      >
        <div className="tw-flex tw-items-center tw-gap-4">
          {columnOptions?.map((option: any, index: number) => (
            <React.Fragment key={index}>
              <div className="tw-flex tw-flex-col tw-items-start">
                <span className="tw-text-xs tw-text-[#97A0AF] tw-font-semibold tw-tracking-wider tw-mb-0.5">
                  {option.title}
                </span>
                <span
                  id={`${option.title}-item-${index}`}
                  className={`tw-text-xs tw-text-[#172B4D] tw-font-semibold ${
                    isLoading[index] ? 'tw-animate-pulse' : ''
                  }`}
                >
                  {isLoading[index] ? (
                    <span className="tw-text-gray-400">Loading...</span>
                  ) : (
                    selected[index] ? selected[index][option.nameColumn] : '-'
                  )}
                </span>
              </div>
              {index < columnOptions.length - 1 && (
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 13L7 7L0.999999 1" stroke="#DADBDD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className={`tw-ml-6 tw-transition-transform tw-duration-200 ${isOpen ? 'tw-rotate-180' : ''}`}>
          <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-inline-block">
            <path d="M1 1L7 7L13 1" stroke="#6D788C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {isOpen && (
        <div id={`${id}-container`} className={`tw-absolute tw-top-full tw-left-0 tw-shadow-lg tw-z-50 tw-mt-1`}>
          <div 
            id={`${id}-content`} 
            className={`tw-relative tw-flex tw-bg-white tw-rounded tw-overflow-hidden tw-border`}
            style={{ height: `${maxDropdownHeight}px` }}
          >
            {columnOptions?.map((option: any, index: number) => {
              const itemCount = data[index] && Array.isArray(data[index]) ? data[index].length : 0;
              const headerHeight = 32;
              const itemHeight = 30;
              const emptyStateHeight = 60;
              const maxVisibleItems = 6;
              
              let calculatedHeight;
              if (itemCount === 0) {
                calculatedHeight = headerHeight + emptyStateHeight;
              } else {
                calculatedHeight = Math.min(
                  headerHeight + (itemCount * itemHeight) + 8,
                  headerHeight + (maxVisibleItems * itemHeight) + 8
                );
              }
              
              return (
                <div 
                  key={index} 
                  className={`tw-min-w-[200px] tw-flex tw-flex-col tw-h-full ${
                    index < columnOptions.length - 1 ? 'tw-border-r tw-border-[#e4e4e8]' : ''
                  } ${isLoading[index] ? 'tw-opacity-50' : ''}`}
                >
                  <ul
                    id={`${option.title}-container`}
                    className={`tw-overflow-auto tw-py-1 tw-bg-white tw-flex-1`}
                    style={{ 
                      maxHeight: `${calculatedHeight}px`,
                      minHeight: `${headerHeight + emptyStateHeight}px`
                    }}
                  >
                    <li id={`${option.title}`}>
                      <div className={`tw-px-2 tw-py-1 tw-text-left tw-w-full tw-font-bold tw-text-gray-dark tw-text-xs tw-border-b`}>
                        {option.title}
                        {/* {isLoading[index] && (
                          <span className="tw-ml-2 tw-text-gray-400 tw-font-normal">
                            (Loading...)
                          </span>
                        )} */}
                      </div>
                    </li>
                    
                    {isLoading[index] ? (
                      <li className="tw-px-2 tw-py-4 tw-text-center">
                        <div className="tw-inline-flex tw-items-center">
                          <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading...
                        </div>
                      </li>
                    ) : (
                      <>
                        {(!data[index] || !Array.isArray(data[index]) || data[index].length <= 0) && (
                          <li id={`${option.title}-no-data`}>
                            <div className={`tw-px-2 tw-py-2 tw-text-left tw-w-full tw-text-gray-500 tw-italic tw-text-xs`}>
                              No {option.title.toLowerCase()}s available
                            </div>
                          </li>
                        )}
                        {data[index] && Array.isArray(data[index]) &&
                          data[index].map((item: any, key: number) => (
                            <li id={`${option.title}-${key}`} key={key}>
                              <button
                                id={`${item[option.nameColumn]}`}
                                onClick={(e) => handleClick(e, index, item)}
                                disabled={isAnyLoading}
                                className={`tw-px-2 tw-py-1 hover:tw-bg-[#f4f5f7] tw-border-t tw-border-[#e4e4e8] tw-text-left tw-w-full tw-text-sm tw-flex tw-items-center tw-justify-between tw-group ${
                                  selected?.[index]?.[option.idColumn] === item?.[option.idColumn] && "tw-bg-[#f4f5f7] tw-text-[#172b4d]"
                                } ${isAnyLoading ? 'tw-cursor-wait' : 'tw-cursor-pointer'}`}
                                title={item[option.nameColumn]}
                              >
                                <span className="tw-truncate tw-pr-2">{item[option.nameColumn]}</span>
                                {selected?.[index]?.[option.idColumn] === item?.[option.idColumn] && (
                                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-ml-2 tw-flex-shrink-0">
                                    <path d="M11 1L4.125 7L1 4.27273" stroke="#172B4D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </button>
                            </li>
                          ))}
                      </>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierachicalDropdown;